import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useAffineMessaging } from "./useAffineMessaging";
import { fetchAffineSso } from "./fetchAffineSso";
import type { AffineEmbedProps, AffineEmbedRef, AffineSsoResponse } from "./types";
import { AFFINE_SSO_ENDPOINT, AFFINE_URL, LOAD_TIMEOUT_MS } from "./types";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";

export const AffineEmbed = forwardRef<AffineEmbedRef, AffineEmbedProps>(
  function AffineEmbed(props, ref) {
    const { className, onReady, onError } = props;

    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [ssoData, setSsoData] = useState<AffineSsoResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Store callbacks in ref to avoid stale closures and unnecessary effect re-runs
    const callbacksRef = useRef({ onReady, onError });
    useEffect(() => {
      callbacksRef.current = { onReady, onError };
    });

    const messaging = useAffineMessaging(iframeRef, { onReady, onError });

    // Get current tenant from auth store
    const currentTenant = useAuthStore((state) => state.currentTenant);

    // Build redirect URL with embed params so Affine detects iframe context
    const embedRedirectTo = useMemo(() => {
      if (!ssoData) return "";
      const url = new URL(ssoData.redirect_to, AFFINE_URL);
      url.searchParams.set("embed", "true");
      url.searchParams.set("parentOrigin", window.location.origin);
      return url.pathname + url.search;
    }, [ssoData]);

    // Build srcDoc from SSO data
    const iframeSrcDoc = useMemo(() => {
      if (!ssoData) return undefined;

      return `<!DOCTYPE html>
<html><head><title>Loading...</title></head>
<body>
<form id="sso" method="POST" action="${AFFINE_SSO_ENDPOINT}">
<input type="hidden" name="sso_token" value="${ssoData.token}" />
<input type="hidden" name="redirect_to" value="${embedRedirectTo}" />
</form>
<script>document.getElementById('sso').submit();</script>
</body></html>`;
    }, [ssoData, embedRedirectTo]);

    // Fetch SSO token from Control Plane
    useEffect(() => {
      if (!currentTenant) return;

      let cancelled = false;
      setIsLoading(true);
      setLoadError(null);

      fetchAffineSso(currentTenant)
        .then((data) => {
          if (cancelled) return;
          setSsoData(data);
          setIsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          const message =
            err instanceof Error ? err.message : "Failed to connect to Affine";
          setLoadError(message);
          setIsLoading(false);
          callbacksRef.current.onError?.(message);
        });

      return () => {
        cancelled = true;
      };
    }, [currentTenant]);

    // Timeout: if not ready after LOAD_TIMEOUT_MS, show error
    useEffect(() => {
      if (messaging.isReady || loadError) return;

      const timeout = setTimeout(() => {
        if (!messaging.isReady) {
          setLoadError("This is taking longer than expected. Please try again.");
          callbacksRef.current.onError?.("Affine load timeout");
        }
      }, LOAD_TIMEOUT_MS);

      return () => clearTimeout(timeout);
    }, [messaging.isReady, loadError]);

    // Track retry generation to discard stale responses
    const retryGenRef = useRef(0);

    // Retry handler: re-fetches SSO token (token is single-use)
    const handleRetry = useCallback(() => {
      setLoadError(null);
      setSsoData(null);
      messaging.reset();
      setIsLoading(true);

      if (!currentTenant) return;

      const gen = ++retryGenRef.current;
      fetchAffineSso(currentTenant)
        .then((data) => {
          if (retryGenRef.current !== gen) return;
          setSsoData(data);
          setIsLoading(false);
        })
        .catch((err) => {
          if (retryGenRef.current !== gen) return;
          const message =
            err instanceof Error ? err.message : "Failed to connect to Affine";
          setLoadError(message);
          setIsLoading(false);
          callbacksRef.current.onError?.(message);
        });
    }, [currentTenant, messaging.reset]);

    // Expose ref API
    useImperativeHandle(
      ref,
      () => ({
        reload: handleRetry,
        get isReady() {
          return messaging.isReady;
        },
      }),
      [handleRetry, messaging.isReady]
    );

    // Loading message: differentiate first-time vs returning
    const loadingMessage = ssoData?.provisioned_now
      ? "Setting up your workspace..."
      : "Loading...";

    // Error state
    if (loadError) {
      return (
        <div
          className={cn(
            "relative flex h-full w-full items-center justify-center bg-background",
            className
          )}
        >
          <div
            className="flex flex-col items-center gap-4 text-center p-6"
            role="alert"
          >
            <svg
              className="h-12 w-12 text-muted-foreground/60"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
              />
            </svg>
            <div>
              <p className="font-medium text-foreground">
                Could not connect to your workspace.
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
            </div>
            <button
              onClick={handleRetry}
              autoFocus
              className="rounded-xs bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "relative h-full w-full bg-background",
          className
        )}
      >
        {/* Loading overlay — fades out when ready */}
        <div
          className={cn(
            "absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-background transition-opacity duration-200 motion-reduce:transition-none",
            messaging.isReady
              ? "pointer-events-none opacity-0"
              : "opacity-100"
          )}
          role="status"
          aria-live="polite"
        >
          <AnimatedStarburst size={48} />
          <span className="text-sm text-muted-foreground">
            {isLoading ? "Connecting..." : loadingMessage}
          </span>
        </div>

        {/* Affine iframe — hidden until srcDoc is ready */}
        {iframeSrcDoc && (
          <iframe
            ref={iframeRef}
            srcDoc={iframeSrcDoc}
            className={cn(
              "absolute inset-0 h-full w-full border-none transition-opacity duration-200 motion-reduce:transition-none",
              messaging.isReady ? "opacity-100" : "opacity-0"
            )}
            title="Affine knowledge base"
            allow="clipboard-read; clipboard-write"
            onLoad={messaging.handleIframeLoad}
          />
        )}
      </div>
    );
  }
);

AffineEmbed.displayName = "AffineEmbed";
